import {getPlayersGroupUID, getSimplexUnitName} from 'helpers/simplex-helper';
import {calcAndRoundUpValByPct} from 'helpers/common-helper';
import RuleBuilder from 'services/simplex/rule-builder';
import {StackItemMarker} from 'components/organisms/singlegame-forced-stacking/services/stack-item-marker';
import {addRoleSuffix} from 'helpers/simplex-helper';

function createCombinationsRules(params) {
    const {stacking, groupedPlayersByTeams, modesConfig} = params;
    const combinations = stacking.getCheckedCombinations();

    const rulesByTeams = createRulesByTeams(combinations);

    const comboRules = createComboRules(combinations, rulesByTeams);

    function isComboRuleCreatable(rulesByTeams, stackItem) {
        if (isEmpty(rulesByTeams[stackItem.teamName])) {
            return false;
        }

        return stackItem.playersQuantity <= Object.keys(rulesByTeams[stackItem.teamName].row).length;
    }

    function createRulesByTeams(combinations) {
        const rulesByTeams = {};

        Object.keys(combinations).map((comboKey) => {
            const {firstTeamName, secondTeamName} = combinations[comboKey];

            [firstTeamName, secondTeamName].map((teamName) => {
                rulesByTeams[teamName] ??= createTeamRule(teamName);
            });
        });

        return rulesByTeams;
    }

    function createTeamRule(teamName) {
        const players = groupedPlayersByTeams[teamName] || [];
        const ruleBuilder = RuleBuilder.fromAtLeast(0);

        players.map((player) => {
            modesConfig.all({asArr: true}).map((modeConfig) => {
                ruleBuilder.addVar(
                    addRoleSuffix({
                        prefix: getSimplexUnitName(player.playerId),
                        role: modeConfig.getRole(),
                    }),
                    1,
                );
            });
        });

        return ruleBuilder.isRuleCreatable() ? ruleBuilder.createRule() : {};
    }

    function createComboRules(combinations, rulesByTeams) {
        const comboRules = {};

        Object.keys(combinations).map((comboKey) => {
            const combo = combinations[comboKey];
            comboRules[comboKey] = {rules: [], groupNames: []};

            const items = [
                {
                    teamName: combo.firstTeamName,
                    playersQuantity: combo.firstTeamPlayersQuantity,
                },
                {
                    teamName: combo.secondTeamName,
                    playersQuantity: combo.secondTeamPlayersQuantity,
                },
            ];

            items.map((item) => {
                if (!isComboRuleCreatable(rulesByTeams, item)) {
                    return;
                }

                const groupName = getGroupName(item.teamName, comboKey);
                const teamRule = cp(rulesByTeams[item.teamName]);

                teamRule.row[groupName] = -1 * item.playersQuantity;

                comboRules[comboKey].rules.push(teamRule);
                comboRules[comboKey].groupNames.push(groupName);
            });
        });

        Object.keys(comboRules).map((comboKey) => {
            const {groupNames} = comboRules[comboKey];
            const ruleBuilder = RuleBuilder.fromExactly(groupNames.length);

            groupNames.map((groupName) => {
                ruleBuilder.addVar(groupName, 1);
            });

            comboRules[comboKey].rules.push(ruleBuilder.createRule());
        });

        return comboRules;
    }

    function getGroupName(teamName, comboKey) {
        const groupName = getPlayersGroupUID(teamName, comboKey);

        return StackItemMarker.setSuffix(groupName);
    }

    return {...comboRules};
}

function buildCombinationsQuantity(params) {
    const {stacking, lineupsQuantity} = params;

    const combinations = stacking.getCheckedCombinations();

    const combinationsQuantity = [];
    Object.keys(combinations).map((comboKey) => {
        const combo = combinations[comboKey];

        combinationsQuantity.push({
            comboKey,
            lineupsQuantity: calcAndRoundUpValByPct(lineupsQuantity.getLineupsQuantity(), combo.exposure),
        });
    });

    return [...combinationsQuantity];
}

class RulesBuilder {
    constructor(dependencies) {
        this._dependencies = {...dependencies};
    }

    createCombinationsRules(params) {
        const {stacking, groupedPlayersByTeams} = this._dependencies;
        return createCombinationsRules({
            stacking,
            groupedPlayersByTeams,
            ...params,
        });
    }

    buildCombinationsQuantity(params) {
        const {stacking} = this._dependencies;
        return buildCombinationsQuantity({stacking, ...params});
    }
}

export {RulesBuilder};
